import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MobileTailwind from "./MobileTailwind";
import OrderItemCard, { OrderItem } from "./components/OrderItemCard";
import { getEncryptedSessionData, setEncryptedSessionData } from "../Services/SessionStorageService";
import { getPosOrderCacheKey, posSocket } from "./Tables";
import SubHeader from "./components/SubHeader";
import WebService from "../Services/WebService";
import { toast } from "react-toastify";
import { useCart } from "./CartContext";

const OrderDetails = () => {
  const navigate = useNavigate();
  const { tableNo = "T1" } = useParams<{ tableNo: string }>();
  const tenantId = localStorage.getItem("tenantId") || "default";
  const [loading, setLoading] = useState(false);
  const cart = useCart();

  // Cache se real menu items lo
  const posOrders = getEncryptedSessionData<Record<string, any>>(getPosOrderCacheKey(tenantId)) || {};
  const tableData = posOrders[tableNo];

  // Group duplicate items by menu_id or id and sum their quantities
  const mergedMenus: any[] = [];
  const rawMenus = tableData?.menus || [];
  rawMenus.forEach((item: any, idx: number) => {
    const key = String(item.menu_id || item.id || `item_${idx}`);
    const existing = mergedMenus.find((m) => String(m.menu_id || m.id) === key);
    if (existing) {
      existing.quantity = Number(existing.quantity || 0) + Number(item.quantity || 1);
    } else {
      mergedMenus.push({
        ...item,
        quantity: Number(item.quantity || 1),
      });
    }
  });

  // Pre-load QR menus into cart lines if the cart is currently empty for this table
  useEffect(() => {
    if (tableData?.type === "QR_ORDER" && tableNo) {
      cart.setTable(tableNo);
      const existingCarts = getEncryptedSessionData<Record<string, any>>(`waiter_carts_${tenantId}`) || {};
      const thisTableCart = existingCarts[tableNo] || [];
      if (thisTableCart.length === 0 && rawMenus.length > 0) {
        const cartLines = rawMenus.map((item: any, idx: number) => {
          const dishId = String(item.menu_id || item.id || item.item_id || `item_${idx}`);
          const dishCode = Number(item.code || item.custom_code || 0);
          const dish = {
            id: dishId,
            name: item.name || item.item_name || item.menu_name || "Item",
            code: dishCode,
            price: Number(item.gross_price ?? item.price ?? item.unit_price ?? 0),
            type: (item.type || (String(item.food_type || "").toLowerCase().includes("non") ? "nonveg" : "veg")) as "veg" | "nonveg",
          };
          return {
            key: `${dish.id}-${dish.code}`,
            dish: dish,
            quantity: Number(item.quantity || 1),
            note: item.special_note || item.note || "",
          };
        });
        cart.setCartLines(tableNo, cartLines);
      }
    }
  }, [tableData, tableNo, tenantId, rawMenus]);

  const handlePlaceOrder = () => {
    if (!tableNo) {
      toast.error("Please select a table");
      return;
    }
    setLoading(true);

    const userInfoStr = localStorage.getItem("user_info");
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;

    const menusPayload = rawMenus.map((item: any, idx: number) => ({
      id: item.menu_id || item.id || item.item_id || `item_${idx}`,
      quantity: Number(item.quantity || 1),
      gross_price: Number(item.gross_price ?? item.price ?? item.unit_price ?? 0),
      special_note: item.special_note || item.note || "",
    }));

    WebService.postAPI({
      action: `/create/waiter-order`,
      body: {
        table_no: tableNo,
        tenantId: tenantId,
        menus: menusPayload,
        waiter_id: userInfo?.id || "",
      },
      id: "saveUserButton",
    })
      .then((res: any) => {
        setLoading(false);
        toast.success("Order placed successfully!");

        // Update cache to set POS_ORDER type
        const posKey = getPosOrderCacheKey(tenantId);
        const existing = getEncryptedSessionData<Record<string, any>>(posKey) || {};
        const tableCache = existing[tableNo];

        existing[tableNo] = {
          ...(tableCache || {}),
          menus: rawMenus,
          timestamp: new Date().toISOString(),
          type: "POS_ORDER",
          loungeId: tableCache?.loungeId || null,
          tokenNumber: res?.token_number || tableCache?.tokenNumber || "",
          isNew: true,
        };
        setEncryptedSessionData(posKey, existing);

        // Clear QR order from cache
        const qrOrderCacheKey = `qr_table_orders_${tenantId}`;
        const cachedQrOrders = getEncryptedSessionData<Record<string, any>>(qrOrderCacheKey) || {};
        if (cachedQrOrders[tableNo]) {
          delete cachedQrOrders[tableNo];
          setEncryptedSessionData(qrOrderCacheKey, cachedQrOrders);
        }

        // Notify socket
        try {
          const s = posSocket();
          const emitPayload = {
            table_no: tableNo,
            lounge_id: tableCache?.loungeId || 1,
            token_number: res?.token_number || "",
            action: "add_menu_items_on_table",
            menus: menusPayload,
            waiter_id: userInfo?.id || "",
            captain_id: "",
            persons: 1,
            special_note: "",
          };
          if (s.connected) {
            s.emit("waiterOrderCreated", emitPayload);
          } else {
            s.once("connect", () => s.emit("waiterOrderCreated", emitPayload));
            s.connect();
          }
        } catch (e) {
          console.error("Socket emit failed:", e);
        }

        // Clear the cart for this table
        cart.clear();

        navigate("/mobile/order-success");
      })
      .catch((error: any) => {
        setLoading(false);
        toast.error(error?.message || "Failed to place order");
      });
  };

  const cachedItemsKey = `menu_items_swr_${tenantId}`;
  const cachedMenuItems = getEncryptedSessionData<any[]>(cachedItemsKey) || [];
  const menuGrossPriceMap: Record<string, number> = {};
  cachedMenuItems.forEach((m: any) => {
    if (m.id) {
      menuGrossPriceMap[String(m.id)] = Number(m.gross_price ?? m.price ?? 0);
    }
  });

  const cachedMenus: OrderItem[] = mergedMenus.map((item: any, idx: number) => {
    const itemTime = item.created_at || item.createdAt || item.ordered_at || item.orderedAt || tableData?.timestamp;
    const mId = String(item.menu_id || item.id);
    const lookupGrossPrice = menuGrossPriceMap[mId];
    return {
      id: String(item.menu_id || item.id || idx),
      code: Number(item.menu_id || item.id || idx),
      name: item.name || item.item_name || item.menu_name || "Item",
      qty: Number(item.quantity || 1),
      price: Number(lookupGrossPrice ?? item.gross_price ?? item.price ?? 0),
      type: item.type || "veg",
      orderedAt: itemTime
        ? new Date(itemTime).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        })
        : "--",
      served: false,
    };
  });

  const items = cachedMenus;

  return (
    <>
      <MobileTailwind />
      <div id="mobile-root" className="min-h-screen w-full bg-black flex justify-center">
        <div
          className="relative w-full min-h-screen overflow-x-hidden"
          style={{ backgroundColor: "#F4F4F5" }}
        >
          {/* Header */}
          <SubHeader title={`Order Details (${tableNo})`} />

          <div className="px-3 pt-3 pb-[100px] flex flex-col gap-3">
            {items.length === 0 ? (
              <p className="text-center font-opensans text-[14px] text-[#8F9098] mt-6">
                No items in this order.
              </p>
            ) : (
              items.map((it) => (
                <OrderItemCard key={it.id} item={it} />
              ))
            )}
          </div>

          {/* Sticky bottom CTA */}
          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full bg-white px-3 py-4 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] flex gap-3">
            <button
              type="button"
              disabled={loading}
              onClick={() => navigate(`/mobile/menu/${tableNo}`)}
              className={`${tableData?.type === "QR_ORDER"
                ? "flex-1 bg-gray-100 text-[#111] border border-[#E6EEFF]"
                : "w-full text-white border-0"
                } rounded-[6px] py-3.5 font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase`}
              style={tableData?.type === "QR_ORDER" ? {} : { backgroundColor: "#F15A29" }}
            >
              + Add More
            </button>
            {tableData?.type === "QR_ORDER" && (
              <button
                type="button"
                disabled={loading}
                onClick={handlePlaceOrder}
                className="flex-1 rounded-[6px] py-3.5 font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase text-white border-0 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ backgroundColor: "#F15A29" }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Placing...
                  </>
                ) : (
                  "Place Order"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetails;